/**
 * Strategy Canvas Selector Registry
 *
 * Single source of truth for ALL strategy canvas/block data-testid values
 * used in strategy-related E2E test helpers.
 *
 * Source components:
 * - src/modules/strategy-blocks/components/StrategyCanvasActions.tsx
 * - src/modules/strategy-blocks/components/BlockFormDialog.tsx
 * - src/modules/strategy-blocks/components/StrategyImportDialog.tsx
 * - src/modules/strategy-blocks/components/StrategyBlocksSection.tsx
 * - src/modules/reactFlow/components/block/FlowBlockNode.tsx
 * - src/modules/reactFlow/components/block/BlockTemplatePanel.tsx
 */
export const STRATEGY_CANVAS = {
  /** Block canvas node — template `node-${blockId}`. */
  blockNode: (blockId: string) => `node-${blockId}`,
  /**
   * Prefix of every block canvas node testid, for `[data-testid^="…"]` sweeps.
   * NOTE the canvas virtualises: a DOM sweep only sees the nodes in the viewport
   * (6 of 32 on the WP7 flagship). Use it to WAIT for the canvas to finish
   * loading, and read the full node set from the ReactFlow store via
   * `getCanvasNodes` — which keys on the block `entityId`, not this document id.
   */
  blockNodeTestIdPrefix: 'node-',
  // ── Canvas Actions dropdown ──
  actionsBtn: 'strategy-canvas-actions-btn',
  /** BLOCK canvas variant of the Actions trigger (screens editor header). */
  actionsBtnScreens: 'strategy-canvas-actions-btn-screens',
  actionsMenu: 'strategy-canvas-actions-menu',
  /** «Advanced…» submenu trigger — export/import/theme actions live behind it (U9). */
  advancedAction: 'strategy-canvas-advanced',
  advancedMenu: 'strategy-canvas-advanced-menu',
  exportAction: 'strategy-canvas-export',
  importAction: 'strategy-canvas-import',
  compileOnboardingAction: 'strategy-canvas-compile-onboarding',
  liftOnboardingAction: 'strategy-canvas-lift-onboarding',
  pullOnboardingAction: 'strategy-canvas-pull-onboarding',
  pushOnboardingAction: 'strategy-canvas-push-onboarding',
  editThemeAction: 'strategy-canvas-edit-theme',
  /** «Replace Theme...» — the theme picker in strategy mode (THEME_V2.strategyThemeSelection*). */
  replaceThemeAction: 'strategy-canvas-replace-theme',

  // ── Version lifecycle (Spec A / D51) ──
  // Actions-menu lifecycle triggers (SI-1) + bake name dialog.
  bakeAction: 'strategy-canvas-bake',
  startNewVersionAction: 'strategy-canvas-start-new-version',
  bakeNameInput: 'strategy-bake-name',
  /** U8 (Spec D2): operator description field in the bake dialog. */
  bakeDescriptionInput: 'strategy-bake-description',
  bakeConfirm: 'strategy-bake-confirm',
  // Version switcher dropdown (mirrors onboarding version dropdown).
  versionSwitcherBtn: 'strategy-version-switcher-btn',
  versionSwitcherMenu: 'strategy-version-switcher-menu',
  versionCompare: 'strategy-version-compare',
  /** Template: `strategy-version-item-${versionId}` */
  versionItem: (versionId: string) => `strategy-version-item-${versionId}`,
  /** U8 (Spec D2): per-row Fork — template `strategy-version-fork-${versionId}` */
  versionFork: (versionId: string) => `strategy-version-fork-${versionId}`,
  // Read-only version view dialog + cherry-pick.
  versionViewDialog: 'strategy-version-view-dialog',
  /** Template: `strategy-version-view-block-${blockId}` */
  versionViewBlock: (blockId: string) => `strategy-version-view-block-${blockId}`,
  /** Template: `strategy-version-cherrypick-${blockId}` */
  versionCherryPick: (blockId: string) => `strategy-version-cherrypick-${blockId}`,
  // Two-version diff dialog.
  versionDiffDialog: 'strategy-version-diff-dialog',
  versionDiffBase: 'strategy-version-diff-base',
  versionDiffTarget: 'strategy-version-diff-target',
  versionDiffCounts: 'strategy-version-diff-counts',
  versionDiffTable: 'strategy-version-diff-table',
  /** Template: `strategy-version-diff-block-${blockId}` */
  versionDiffBlock: (blockId: string) => `strategy-version-diff-block-${blockId}`,
  versionDiffVariables: 'strategy-version-diff-variables',
  /** Two-axis divergence chip on the strategy blocks canvas (A5-2). */
  divergenceChip: 'strategy-divergence-chip-strategy',
  /** Cross-strategy immutable input lineage indicator and review-only details. */
  lineageChip: 'strategy-lineage-chip',
  lineagePopover: 'strategy-lineage-popover',
  /**
   * F-LIVE2-44: the fork dialog's OWN surfaces. Until fix wave 9 the fork
   * announced both its completion and the `IMMUTABLE_PREDECESSOR_REQUIRED`
   * refusal through raw browser `alert()`s (third sighting of the channel
   * F-LIVE2-23a / F-LIVE2-28 flagged).
   */
  forkDialog: 'strategy-fork-dialog',
  forkError: 'strategy-fork-error',
  forkErrorCode: 'strategy-fork-error-code',
  /** Spec D4 (U6): persistent "Onboarding ↗" chip (keys on lastCompiledTo). */
  openOnboardingChip: 'strategy-open-onboarding-chip',
  /** Spec D4 (U11): stale-citation chip on a block node — template `block-cites-old-${blockId}` */
  blockCitesOld: (blockId: string) => `block-cites-old-${blockId}`,
  /** Spec D4 (U11): rebase action in the node toolbar (visible slot). */
  rebaseBlockAction: 'action-rebase-block',

  /**
   * Canvas notice snackbar (Alert). Shared success/error surface for sync
   * (compile/lift/push) AND the Block Examples Library flows — library import
   * confirmation and the FP-1 silent-insert confirmation both land here.
   */
  syncSnackbar: 'strategy-sync-snackbar',

  // ── Sync Confirm Dialog (pull/push/compile/lift shared plan view) ──
  syncConfirmDialog: 'strategy-sync-confirm-dialog',
  syncConfirm: 'strategy-sync-confirm',
  syncPlanCounts: 'strategy-sync-plan-counts',
  /** Template: `strategy-sync-count-${create|replace|preserve|delete}` */
  syncCount: (action: string) => `strategy-sync-count-${action}`,
  syncWarnings: 'strategy-sync-warnings',
  syncError: 'strategy-sync-error',
  // Per-screen diff table
  syncDiffTable: 'strategy-sync-diff-table',
  /** Template: `strategy-sync-block-group-${blockEntityId}` */
  syncBlockGroup: (blockEntityId: string) => `strategy-sync-block-group-${blockEntityId}`,
  /** Template: `strategy-sync-row-${screenId}` */
  syncRow: (screenId: string) => `strategy-sync-row-${screenId}`,
  /** Template: `strategy-sync-row-kind-${screenId}-${copy|edge|asset}` */
  syncRowKind: (screenId: string, kind: string) => `strategy-sync-row-kind-${screenId}-${kind}`,
  /** Template: `strategy-sync-row-warning-${screenId}` — no-baseline badge */
  syncRowWarning: (screenId: string) => `strategy-sync-row-warning-${screenId}`,
  /** Template: `strategy-sync-conflict-toggle-${screenId}` — Keep funnel | Take strategy */
  syncConflictToggle: (screenId: string) => `strategy-sync-conflict-toggle-${screenId}`,
  // Variable diff section
  syncVariablesSection: 'strategy-sync-variables-section',
  /** Template: `strategy-sync-variable-row-${variableId}` */
  syncVariableRow: (variableId: string) => `strategy-sync-variable-row-${variableId}`,
  /** Template: `strategy-sync-variable-resolution-${variableId}` — preserve|overwrite|fork|drop select */
  syncVariableResolution: (variableId: string) => `strategy-sync-variable-resolution-${variableId}`,
  // AI asset diff section
  syncAssetsSection: 'strategy-sync-assets-section',
  /** Template: `strategy-sync-asset-row-${screenId}-${slotKey}` */
  syncAssetRow: (screenId: string, slotKey: string) => `strategy-sync-asset-row-${screenId}-${slotKey}`,
  /** Template: `strategy-sync-asset-choice-${screenId}-${slotKey}` — selected experiment picker */
  syncAssetChoice: (screenId: string, slotKey: string) => `strategy-sync-asset-choice-${screenId}-${slotKey}`,
  // Loose (unassigned) screens picker — lift/pull confirm only
  syncLooseSection: 'strategy-sync-loose-section',
  /** Template: `strategy-sync-loose-row-${screenId}` */
  syncLooseRow: (screenId: string) => `strategy-sync-loose-row-${screenId}`,
  /** Template: `strategy-sync-loose-choice-${screenId}` — keep-loose/existing-block/new-block Autocomplete */
  syncLooseChoice: (screenId: string) => `strategy-sync-loose-choice-${screenId}`,

  // ── Lift Dialog ──
  liftDialog: 'strategy-lift-dialog',
  liftFunnelId: 'strategy-lift-funnel-id',
  liftVersionId: 'strategy-lift-version-id',
  liftTargetStrategyId: 'strategy-lift-target-strategy-id',
  liftName: 'strategy-lift-name',
  liftReplaceExisting: 'strategy-lift-replace-existing',
  liftSubmit: 'strategy-lift-submit',
  /** Two-step lift: plan review step container + final confirm */
  liftPlan: 'strategy-lift-plan',
  liftConfirm: 'strategy-lift-confirm',

  // ── Compile Dialog ──
  compileDialog: 'strategy-compile-dialog',
  compileName: 'strategy-compile-name',
  compileVersionName: 'strategy-compile-version-name',
  compileThemeChoice: 'strategy-compile-theme-choice',
  compileProjectThemeId: 'strategy-compile-project-theme-id',
  compileSubmit: 'strategy-compile-submit',
  /** Two-step compile: plan review step container + final confirm */
  compilePlan: 'strategy-compile-plan',
  compileConfirm: 'strategy-compile-confirm',
  compileHealthSummary: 'compile-health-summary',
  compileHealthStrategyLine: 'compile-health-strategy-line',
  compileHealthFindings: 'compile-health-findings',

  // ── Strategy Health Panel ──
  canvasHealthBadge: 'canvas-health-badge',
  canvasHealthPanel: 'canvas-health-panel',
  canvasHealthRefresh: 'canvas-health-refresh',
  canvasHealthClose: 'canvas-health-close',
  canvasRepairStrategy: 'canvas-repair-strategy',
  canvasRepairPanel: 'canvas-repair-panel',
  canvasRepairScope: 'canvas-repair-scope',
  canvasRepairDryRun: 'canvas-repair-dry-run',
  canvasRepairChecksum: 'canvas-repair-checksum',
  canvasRepairConfirm: 'canvas-repair-confirm',
  canvasRepairResetLedger: 'canvas-repair-reset-ledger',
  canvasRepairResetConfirm: 'canvas-repair-reset-confirm',
  canvasRepairApply: 'canvas-repair-apply',
  canvasRepairCancel: 'canvas-repair-cancel',
  canvasRepairMessage: 'canvas-repair-message',

  // ── Block Templates panel catalog (UXR C1/C3) ──
  /** Panel gear — the ONLY entry to the subtype manager (Q1). */
  templateSettings: 'strategy-template-panel-gear',
  /** Template: `strategy-template-subtype-card-${slug}` — project subtype card in the panel. */
  templateItemSubtype: (slug: string) => `strategy-template-subtype-card-${slug}`,
  /** Template: `strategy-template-subtype-badge-${slug}` — slug badge on a subtype card. */
  templateSubtypeBadge: (slug: string) => `strategy-template-subtype-badge-${slug}`,
  /** Template: `strategy-template-disabled-badge-${slug}` — disabled marker on a subtype card. */
  templateDisabledBadge: (slug: string) => `strategy-template-disabled-badge-${slug}`,
  /** Template: `strategy-template-add-${key}` — click-to-add button (key = blockType or subtype slug). */
  templateAddButton: (key: string) => `strategy-template-add-${key}`,
  /** Ghost card at the end of the subtype group — opens the manager in create mode. */
  templateNewSubtype: 'strategy-template-new-subtype',

  // ── Block subtype catalog (B5; manager dialog since UXR C2) ──
  subtypeManagerDialog: 'strategy-subtype-manager-dialog',
  subtypeManagerClose: 'strategy-subtype-manager-close',
  subtypeGuidance: 'strategy-subtype-guidance',
  subtypeExampleAdd: 'strategy-subtype-example-add',
  subtypeExampleList: 'strategy-subtype-example-list',
  /** Template: `strategy-subtype-example-${index}` */
  subtypeExample: (index: number) => `strategy-subtype-example-${index}`,
  subtypeNew: 'strategy-subtype-new',
  subtypeSave: 'strategy-subtype-save',
  subtypeDelete: 'strategy-subtype-delete',
  subtypeName: 'strategy-subtype-name',
  subtypeSlug: 'strategy-subtype-slug',
  subtypeDescription: 'strategy-subtype-description',
  subtypePlaybook: 'strategy-subtype-playbook',
  subtypeEnabled: 'strategy-subtype-enabled',
  subtypeInstructionAdd: 'strategy-subtype-instruction-add',
  subtypeInstructionList: 'strategy-subtype-instruction-list',
  /** Template: `strategy-subtype-row-${subtypeId}` */
  subtypeRow: (subtypeId: string) => `strategy-subtype-row-${subtypeId}`,
  /** Template: `strategy-subtype-instruction-${index}` */
  subtypeInstruction: (index: number) => `strategy-subtype-instruction-${index}`,
  /** Template: `strategy-block-subtype-badge-${blockId}` */
  blockSubtypeBadge: (blockId: string) => `strategy-block-subtype-badge-${blockId}`,

  // ── Staged library adaptation inspector ──
  libraryDraftBadge: (blockId: string) => `library-draft-badge-${blockId}`,
  libraryDraftState: (blockId: string) => `library-draft-state-${blockId}`,
  createBlockScreens: (blockId: string) => `create-with-ai-${blockId}`,
  libraryAdaptationInspector: 'library-adaptation-inspector',
  libraryAdaptationClose: 'library-adaptation-close',
  libraryAdaptationSource: 'library-adaptation-source',
  libraryAdaptationTopology: 'library-adaptation-topology',
  libraryAdaptationEvidenceMode: 'library-adaptation-evidence-mode',
  libraryAdaptationEvidenceSummary: 'library-adaptation-evidence-summary',
  libraryAdaptationEvidenceItem: (kind: string, id: string) => `library-adaptation-evidence-${kind}-${id}`,
  libraryAdaptationResolvedEvidenceItem: (kind: string, id: string) => `library-adaptation-resolved-evidence-${kind}-${id}`,
  libraryAdaptationModel: 'library-adaptation-model',
  libraryAdaptationInstruction: 'library-adaptation-instruction',
  libraryAdaptationVariableConflicts: 'library-adaptation-variable-conflicts',
  libraryAdaptationVariableResolution: (variableId: string) => `library-adaptation-variable-${variableId}`,
  libraryAdaptationRefresh: 'library-adaptation-refresh',
  libraryAdaptationRemove: 'library-adaptation-remove',
  libraryAdaptationSubmit: 'library-adaptation-submit',

  // ── Block Form Dialog ──
  blockFormDialog: 'block-form-dialog',
  blockFormName: 'block-form-name',
  blockFormType: 'block-form-type',
  blockFormDescription: 'block-form-description',
  blockFormSubtype: 'block-form-subtype',
  blockFormSubtypeWarning: 'block-form-subtype-warning',
  blockFormInstructionAdd: 'block-form-instruction-add',
  blockFormInstructionList: 'block-form-instruction-list',
  /** Template: `block-form-instruction-${index}` */
  blockFormInstruction: (index: number) => `block-form-instruction-${index}`,
  blockFormUseAI: 'block-form-use-ai',
  blockFormAIStyle: 'block-form-ai-style',
  blockFormAIPrompt: 'block-form-ai-prompt',
  blockFormSaveBtn: 'block-form-save-btn',
  blockFormExportBtn: 'block-form-export-btn',
  /** The form's ONE inline failure surface (save refusal or refused generation). */
  blockFormErrorAlert: 'block-form-error-alert',

  // ── Block Screen Generation Dialog ──
  blockScreenGenerationDialog: 'block-screen-generation-dialog',
  blockScreenGenerationModel: 'block-screen-generation-model',
  blockScreenGenerationDescription: 'block-screen-generation-description',
  blockScreenGenerationBlockDescription: 'block-screen-generation-block-description',
  blockScreenGenerationSubmit: 'block-screen-generation-submit',
  /** HB-1 client: the caption naming WHICH model is in the box, and why. */
  blockScreenGenerationModelSource: 'block-screen-generation-model-source',
  // ── C7-3 operator data override (opt-in accordion inside the dialog) ──
  blockScreenGenerationOverrideAccordion: 'block-screen-generation-override-accordion',
  blockScreenGenerationOverrideBadge: 'block-screen-generation-override-badge',
  strategyGroundingOriginLabel: 'strategy-grounding-origin-label',
  // ── HB-5: the override speaks the create wizard's vocabulary ──
  /** Segmented switch between the two overridable planes. */
  blockOverrideSegment: 'block-override-segment',
  /** Template: `block-override-segment-${'theses' | 'social-proof'}`. */
  blockOverrideSegmentOption: (segment: 'theses' | 'social-proof') => `block-override-segment-${segment}`,
  /** Thesis table root; rows/search/summary take the SelectionTable suffixes. */
  blockOverrideTheses: 'block-override-theses',
  blockOverrideThesesSummary: 'block-override-theses-summary',
  blockOverrideSocialProof: 'block-override-social-proof',
  blockOverrideSocialProofSummary: 'block-override-social-proof-summary',
  /** Read-only variables fact inside the accordion. */
  blockOverrideVariables: 'block-override-variables',
  /** «+N more» — the clamp on a long variable package, never a silent trim. */
  blockOverrideVariablesMore: 'block-override-variables-more',
  /** One-click return to the preloaded selection (designer invariant 3). */
  blockOverrideReset: 'block-override-reset',
  /** Ruling HB5-R1: the inline refusal for a scope the tick left with nothing. */
  blockOverrideStarved: 'block-override-starved',
  /** Template: `block-override-starved-${'global' | taskId}`. */
  blockOverrideStarvedScope: (scopeId: string) => `block-override-starved-${scopeId}`,
  /** Template: `block-override-starved-fix-${'global' | taskId}` — its way out. */
  blockOverrideStarvedFix: (scopeId: string) => `block-override-starved-fix-${scopeId}`,
  /** Explains the preload for a block that cited nothing of its own. */
  blockOverrideNoCitations: 'block-override-no-citations',
  /** Grounding-panel section tags — only the two overridable sections carry one. */
  strategyGroundingTheses: 'strategy-grounding-theses',
  strategyGroundingThesesTag: 'strategy-grounding-theses-tag',
  strategyGroundingSocialProofTag: 'strategy-grounding-social-proof-tag',
  // ── HB-6: one entry point — the Edit Block form opens the dialog ──
  blockFormUpdateWithAI: 'block-form-update-with-ai',
  blockFormUpdateWithAIReason: 'block-form-update-with-ai-reason',

  // ── C7-2 Clone Block popover (node menu item testid is auto-derived:
  //    `menu-action-clone-block` from the NodeAction key) ──
  cloneBlockMenuAction: 'menu-action-clone-block',
  cloneBlockPopover: 'clone-block-popover',
  cloneBlockCopyScreens: 'clone-block-copy-screens',
  cloneBlockKeepOutgoing: 'clone-block-keep-outgoing',
  cloneBlockIncomingDisconnected: 'clone-block-incoming-disconnected',
  cloneBlockIncomingFork: 'clone-block-incoming-fork',
  /** "Clone only" — plain clone, no generation. */
  cloneBlockSubmit: 'clone-block-submit',
  // ── C7-4 variant fan-out (same popover) ──
  cloneBlockVariantCountGroup: 'clone-block-variant-count',
  /** Template: `clone-block-variant-count-${n}` (n = 1..3). */
  cloneBlockVariantCount: (count: number) => `clone-block-variant-count-${count}`,
  /** Template: `clone-block-variant-angle-${i}` (1-based). */
  cloneBlockVariantAngle: (index: number) => `clone-block-variant-angle-${index}`,
  cloneBlockGenerateVariants: 'clone-block-generate-variants',
  /** Variant-generation model selector (user-connected models only). */
  cloneBlockModel: 'clone-block-model',
  /** Template: `block-variant-of-${sourceBlockId}` — canvas node chip (AC-D01). */
  blockVariantOf: (sourceBlockId: string) => `block-variant-of-${sourceBlockId}`,

  // ── Block Examples Library: palette EXAMPLES group ──
  /** Template: `block-template-item-example-${exampleId}` */
  templateItemExample: (exampleId: string) => `block-template-item-example-${exampleId}`,
  /** Template: `template-example-origin-${exampleId}` — My/System chip. */
  templateExampleOriginBadge: (exampleId: string) => `template-example-origin-${exampleId}`,
  /** Template: `template-example-warn-${exampleId}` — assets-rebuild chip. */
  templateExampleWarnBadge: (exampleId: string) => `template-example-warn-${exampleId}`,
  /** Template: `template-example-details-${exampleId}` — ⓘ opens the details dialog. */
  templateExampleDetails: (exampleId: string) => `template-example-details-${exampleId}`,
  // ── Block Examples Library: example details (dialog + in-browser swap) ──
  exampleDetailsDialog: 'example-details-dialog',
  exampleDetailsView: 'example-details-view',
  exampleDetailsBlueprintSummary: 'example-details-blueprint-summary',
  exampleDetailsFit: 'example-details-fit',
  exampleDetailsScreens: 'example-details-screens',
  exampleDetailsBackToStrip: 'example-details-back-to-strip',
  exampleDetailsAdd: 'example-details-add',
  exampleDetailsBroken: 'example-details-broken',
  /** Template: `example-details-screens-screen-${index}` — filmstrip thumb. */
  exampleDetailsScreen: (index: number) => `example-details-screens-screen-${index}`,
  exampleDetailsEnlarged: 'example-details-enlarged',
  /** Browser card «Details» button: `library-browser-details-${exampleId}`. */
  libraryBrowserDetails: (exampleId: string) => `library-browser-details-${exampleId}`,
  /** Back arrow from the in-browser details swap. */
  libraryBrowserDetailsBack: 'library-browser-details-back',
  templateBrowseLibrary: 'template-browse-library',
  templateExamplesEmptyCta: 'template-examples-empty-cta',

  // ── Block Examples Library: explicit secondary immediate/as-is dialog ──
  insertBuildingBlockDialog: 'insert-building-block-dialog',
  /** Template: `insert-block-row-${exampleVarId}` — one mapping row per contract var. */
  insertBlockRow: (varId: string) => `insert-block-row-${varId}`,
  /** Primary: «Insert & adapt with AI». */
  insertBlockAdapt: 'insert-block-adapt',
  /** Secondary text action: «Insert as-is». */
  insertBlockAsIs: 'insert-block-as-is',
  /** Adaptation model selector (prefilled with the project default). */
  insertBlockModel: 'insert-block-model',
  /** Canvas pending snackbar while the from-library insert roundtrip runs. */
  exampleInsertPending: 'example-insert-pending',

  // ── Block Examples Library: Save as example (node menu item testid is
  //    auto-derived: `menu-action-save-as-example` from the NodeAction key) ──
  nodeMoreActions: 'more-actions-btn',
  nodeActionSaveAsExample: 'menu-action-save-as-example',
  saveAsExampleDialog: 'save-as-example-dialog',
  saveAsExampleSummary: 'save-as-example-summary',
  saveAsExampleName: 'save-as-example-name',
  saveAsExampleDescription: 'save-as-example-description',
  saveAsExampleTags: 'save-as-example-tags',
  saveAsExampleOversize: 'save-as-example-oversize',
  saveAsExampleSubmit: 'save-as-example-submit',

  // ── Block Examples Library: browser dialog (Import / Pick, Phase 2) ──
  libraryBrowserDialog: 'library-browser-dialog',
  libraryBrowserSearch: 'library-browser-search',
  libraryBrowserSelectAll: 'library-browser-select-all',
  /** Template: `library-browser-filter-${origin}` (origin: all|system|my) */
  libraryBrowserFilter: (origin: string) => `library-browser-filter-${origin}`,
  /** Template: `library-browser-card-${exampleId}` */
  libraryBrowserCard: (id: string) => `library-browser-card-${id}`,
  /** Template: `library-browser-select-${exampleId}` (system-card checkbox) */
  libraryBrowserSelect: (id: string) => `library-browser-select-${id}`,
  /** Template: `library-browser-quick-add-${exampleId}` */
  libraryBrowserQuickAdd: (id: string) => `library-browser-quick-add-${id}`,
  /** Secondary pre-placement AI path: `library-browser-immediate-${exampleId}`. */
  libraryBrowserImmediate: (id: string) => `library-browser-immediate-${id}`,
  exampleDetailsImmediate: 'example-details-immediate',
  /** Template: `library-browser-imported-${systemExampleId}` (Imported badge) */
  libraryBrowserImported: (id: string) => `library-browser-imported-${id}`,
  libraryBrowserImport: 'library-browser-import',

  // ── BlocksListView inside StrategyFormDialog ──
  blocksListAddBtn: 'blocks-list-add-btn',
  blocksListTable: 'blocks-list-table',
  /** Template: `blocks-list-actions-${id}` */
  blocksListActions: (id: string) => `blocks-list-actions-${id}`,
  blocksListMenuEdit: 'blocks-list-menu-edit',
  blocksListMenuClone: 'blocks-list-menu-clone',
  blocksListMenuDelete: 'blocks-list-menu-delete',

  // ── Import Dialog ──
  importDialog: 'strategy-import-dialog',
  importFileToggle: 'strategy-import-file-toggle',
  importFileBtn: 'strategy-import-file-btn',
  importPasteToggle: 'strategy-import-paste-toggle',
  importPasteInput: 'strategy-import-paste-input',
  importValidateBtn: 'strategy-import-validate-btn',
  importSubmitBtn: 'strategy-import-submit-btn',
  importValidationStatus: 'strategy-import-validation-status',
  importError: 'strategy-import-error',

  // ── Blocks Section (table in StrategyFormDialog) ──
  blocksTable: 'strategy-blocks-table',
  blocksCreateBtn: 'strategy-blocks-create-btn',
  /** Template: `block-row-${id}` */
  blockRow: (id: string) => `block-row-${id}`,
  /** Template: `block-edit-${id}` */
  blockEdit: (id: string) => `block-edit-${id}`,
  /** Template: `block-clone-${id}` */
  blockClone: (id: string) => `block-clone-${id}`,
  /** Template: `block-delete-${id}` */
  blockDelete: (id: string) => `block-delete-${id}`,

  // ── Block nodes on canvas (existing testids, centralized reference) ──
  /** Template: `launch-indicator-${id}` */
  launchIndicator: (id: string) => `launch-indicator-${id}`,

  // ── Block Template Panel ──
  templatePanel: 'block-template-panel',
  templateSearch: 'block-template-search',
  templateList: 'block-template-list',
  /** Template: `block-template-item-${blockType}` */
  templateItem: (blockType: string) => `block-template-item-${blockType}`,
  templateNoResults: 'block-template-no-results',
} as const;
